// Payment method types for saved cards + SECH wallet

export type PaymentMethodType = 'TOKEN' | 'CARD'

// Supported billing countries
export type BillingCountry = 'US' | 'CA' | 'MX'

export const BILLING_COUNTRIES: { value: BillingCountry; label: string }[] = [
	{ value: 'US', label: 'United States' },
	{ value: 'CA', label: 'Canada' },
	{ value: 'MX', label: 'Mexico' },
]

// Per-country address field labels, input modes, and constraints
export const COUNTRY_ADDRESS_CONFIG: Record<BillingCountry, {
	stateLabel: string
	postalLabel: string
	postalInputMode: 'numeric' | 'text'
	stateMaxLength: number
	postalMaxLength: number
}> = {
	US: {
		stateLabel: 'State',
		postalLabel: 'ZIP Code',
		postalInputMode: 'numeric',
		stateMaxLength: 2,
		postalMaxLength: 10,   // ZIP+4: 12345-6789
	},
	CA: {
		stateLabel: 'Province',
		postalLabel: 'Postal Code',
		postalInputMode: 'text',
		stateMaxLength: 2,
		postalMaxLength: 7,    // A1A 1A1
	},
	MX: {
		stateLabel: 'State',
		postalLabel: 'Postal Code',
		postalInputMode: 'numeric',
		stateMaxLength: 2,
		postalMaxLength: 5,
	},
}

// Billing address attached to a saved card
export interface BillingInfo {
	firstName: string
	lastName: string
	address1: string
	address2?: string
	city: string
	state: string
	zip: string
	country?: BillingCountry   // defaults to "US"
	email?: string
	phone?: string
}

// Saved credit/debit card from the billing vault
export interface SavedCard {
	id: number
	type: 'CARD'
	cardBrand: string | null
	lastFour: string
	expiryMonth: number
	expiryYear: number
	cardholderName: string | null
	isDefault: boolean
	billing: BillingInfo | null
	createdAt: string
	/**
	 * True when the card's gateway vault became invalid and was recreated by the backend.
	 * The card cannot be charged until the customer re-enters it; the flag clears on the
	 * backend once the card is re-submitted via the update flow.
	 */
	updateRequired?: boolean
}

// The SECH wallet option (virtual — always available, never stored in backend)
export interface SechWalletMethod {
	type: 'TOKEN'
	id: 'sech-wallet'
}

// Union type for any selectable payment method at checkout
export type SelectablePaymentMethod = SechWalletMethod | SavedCard

// Default payment method constant
export const DEFAULT_PAYMENT_METHOD: SechWalletMethod = {
	type: 'TOKEN',
	id: 'sech-wallet',
}

// Request body for adding a card via Direct Post
export interface AddCardRequest {
	ccNumber: string
	ccExp: string       // "MMYY" format
	cvv: string
	billing: BillingInfo
}

// Request body for updating a card (full card + billing required)
export type UpdateCardRequest = AddCardRequest

// Card brand display info
export const CARD_BRAND_LABELS: Record<string, string> = {
	visa: 'Visa',
	mc: 'Mastercard',
	mastercard: 'Mastercard',
	amex: 'American Express',
	disc: 'Discover',
	discover: 'Discover',
	diners: 'Diners Club',
	jcb: 'JCB',
}

export function getCardBrandLabel(brand: string): string {
	return CARD_BRAND_LABELS[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1)
}
